import React, { useEffect, useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  CircularProgress,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  arrayUnion,
  deleteDoc,
  setDoc,
} from 'firebase/firestore';
import { db } from '../firebase'; // Adjust path as necessary
import { useUser } from '../UserContext';

// A lightweight "AssignSectionDialog" that handles a single request's approval
function AssignSectionDialog({
  open,
  onClose,
  onSubmit,
  pennant,
  request,
}) {
  const [availableSections, setAvailableSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(
    request.requestedSection || 'Choose for me'
  );

  useEffect(() => {
    if (!pennant) return;
    const sections = pennant.members ? Object.keys(pennant.members) : [];
    setAvailableSections(sections);

    // If the user requested a valid section, pre-select it
    if (sections.includes(request.requestedSection)) {
      setSelectedSection(request.requestedSection);
    }
  }, [pennant, request]);

  const handleConfirm = () => {
    onSubmit(selectedSection);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Approve Join Request</DialogTitle>
      <DialogContent dividers>
        <Typography gutterBottom>
          Approving {request.memberName} for{' '}
          {pennant.sport
            ? `${pennant.sport} ${pennant.name}`
            : pennant.name || pennant.id}.
        </Typography>
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel id="approve-section-label">Section</InputLabel>
          <Select
            labelId="approve-section-label"
            value={selectedSection}
            label="Section"
            onChange={(e) => setSelectedSection(e.target.value)}
          >
            <MenuItem value="Choose for me">Choose for me</MenuItem>
            {availableSections.map((sectionName) => (
              <MenuItem key={sectionName} value={sectionName}>
                {sectionName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleConfirm}>
          Approve
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function PendingRequests() {
  const { user } = useUser(); // To check if user is admin/staff
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);

  // For the "Approve" flow
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [currentRequest, setCurrentRequest] = useState(null);
  const [currentPennant, setCurrentPennant] = useState(null);

  // Fetch all pending requests across all pennants
  useEffect(() => {
    if (!user || !['admin', 'staff'].includes(user.role)) return; // Only admins/staff can view

    const fetchPendingRequests = async () => {
      setLoading(true);
      try {
        const pennantsSnapshot = await getDocs(collection(db, 'Pennants'));
        const pennants = pennantsSnapshot.docs;

        // Prepare an array of promises to fetch requests from each pennant
        const requestsPromises = pennants.map(async (pennantDoc) => {
          const pennantId = pennantDoc.id;
          const pennantData = pennantDoc.data();

          const requestsSnapshot = await getDocs(
            collection(db, 'Pennants', pennantId, 'requestsToJoin')
          );

          // Map each request to include pennant details (including sport!)
          return requestsSnapshot.docs.map((requestDoc) => ({
            requestId: requestDoc.id,
            pennantId,
            pennantName: pennantData.name || pennantId,
            pennantSport: pennantData.sport || '',
            pennantData, // attach entire doc data for members, etc.
            ...requestDoc.data(),
          }));
        });

        const allRequestsNested = await Promise.all(requestsPromises);
        const allRequests = allRequestsNested.flat();

        setRequests(allRequests);
      } catch (error) {
        console.error('Error fetching pending requests:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPendingRequests();
  }, [user]);

  // Step 1: Open the section-assigning dialog
  const handleApproveDialog = (request) => {
    // We know request.pennantData is the entire doc
    setCurrentPennant({
      id: request.pennantId,
      ...request.pennantData, // includes 'sport', 'members', etc.
    });
    setCurrentRequest(request);
    setApproveDialogOpen(true);
  };

  // Step 2: If user confirms "Approve" in the AssignSectionDialog
  const handleApprove = async (sectionName) => {
    if (!currentRequest || !currentPennant) return;

    const {
      pennantId,
      memberId,
      memberName,
      requestId,
      requestedSection,
      sport: requestedSport,
    } = currentRequest;

    const finalSection = sectionName === 'Choose for me' ? null : sectionName;

    try {
      // 1) If finalSection is set, add them to that section in the Pennant's members object
      if (finalSection) {
        const existingSections = currentPennant.members || {};
        const updatedSection = [
          ...(existingSections[finalSection] || []),
          memberId,
        ];
        const updatedMembers = {
          ...existingSections,
          [finalSection]: updatedSection,
        };

        // update the Pennant doc
        const pennantRef = doc(db, 'Pennants', pennantId);
        await updateDoc(pennantRef, { members: updatedMembers });

        // 2) Update the member's profile
        // Include the sport from the currentPennant or request
        const memberRef = doc(db, 'members', memberId);
        await setDoc(
          memberRef,
          {
            pennants: arrayUnion({
              pennantId,
              name: currentPennant.name,
              sport: currentPennant.sport || requestedSport || '',
              section: finalSection,
            }),
          },
          { merge: true }
        );
      } else {
        // TODO: Fix this section/logic
        // Dont allow Choose for me to be used by Staff
        console.log('No specific section chosen.');
      }

      // 3) Remove the join request doc
      const requestRef = doc(db, 'Pennants', pennantId, 'requestsToJoin', requestId);
      await deleteDoc(requestRef);

      // 4) Refresh the local state to remove the approved request
      setRequests((prev) => prev.filter((r) => r.requestId !== requestId));
      alert(
        `Approved join request for ${memberName} in "${
          currentPennant.sport
            ? `${currentPennant.sport} ${currentPennant.name}`
            : currentPennant.name
        }".`
      );
    } catch (error) {
      console.error('Error approving join request:', error);
      alert('Failed to approve join request. Please try again.');
    } finally {
      setApproveDialogOpen(false);
      setCurrentRequest(null);
      setCurrentPennant(null);
    }
  };

  const handleDeny = async (request) => {
    const { pennantId, requestId, memberName } = request;

    try {
      const requestRef = doc(db, 'Pennants', pennantId, 'requestsToJoin', requestId);
      await deleteDoc(requestRef);

      setRequests((prev) => prev.filter((r) => r.requestId !== requestId));
      alert(`Denied join request for ${memberName}`);
    } catch (error) {
      console.error('Error denying join request:', error);
      alert('Failed to deny join request. Please try again.');
    }
  };

  if (!user || !['admin', 'staff'].includes(user.role)) {
    return <Typography>You do not have access to view this page.</Typography>;
  }

  return (
    <Card>
      <CardHeader title="Pending Join Requests" />
      <CardContent>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center">
            <CircularProgress />
          </Box>
        )}

        {!loading && requests.length === 0 && (
          <Typography>No pending requests.</Typography>
        )}

        {!loading && requests.length > 0 && (
          <List>
            {requests.map((request) => {
              // If request has a sport, prepend it to the pennant name
              const combinedPennantName = request.pennantSport
                ? `${request.pennantSport} ${request.pennantName}`
                : request.pennantName;

              const dateObj = request.requestedAt?.seconds
                ? new Date(request.requestedAt.seconds * 1000)
                : new Date(request.requestedAt || Date.now());

              return (
                <ListItem key={request.requestId} divider>
                  <ListItemText
                    primary={`${request.memberName} (${request.email})`}
                    secondary={`Pennant: ${combinedPennantName} | Requested At: ${dateObj.toLocaleString()} | Section: ${
                      request.requestedSection || 'N/A'
                    }`}
                  />
                  <Button
                    variant="contained"
                    color="success"
                    onClick={() => handleApproveDialog(request)}
                    sx={{ mr: 1 }}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={() => handleDeny(request)}
                  >
                    Deny
                  </Button>
                </ListItem>
              );
            })}
          </List>
        )}
      </CardContent>

      {/* Dialog for approving a single request */}
      {approveDialogOpen && currentPennant && currentRequest && (
        <AssignSectionDialog
          open={approveDialogOpen}
          onClose={() => setApproveDialogOpen(false)}
          onSubmit={handleApprove}
          pennant={currentPennant}
          request={currentRequest}
        />
      )}
    </Card>
  );
}
